import React, { useState, useEffect } from 'react';
import { companyAPI, extractApiError } from '../../services/api';
import AlertBanner from '../common/AlertBanner';
import '../../styles/admin/Modal.css';

/**
 * 企業作成・編集モーダル
 */
const CompanyModal = ({ company, onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    calendar_color: '#007bff',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);

  const isEditMode = !!company;

  // 編集モードの場合、初期値を設定
  useEffect(() => {
    if (company) {
      setFormData({
        company_name: company.company_name || '',
        calendar_color: company.calendar_color || '#007bff',
      });
    }
  }, [company]);

  // フォームの変更ハンドラ
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // バリデーション
  const validate = () => {
    const newErrors = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = '企業名を入力してください';
    }

    if (!formData.calendar_color) {
      newErrors.calendar_color = 'カラーを選択してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 保存処理
  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        // 更新
        setLastPayload({ type: 'update', id: company.company_id, data: formData });
        await companyAPI.updateCompany(company.company_id, formData);
        alert('企業情報を更新しました');
      } else {
        // 新規作成
        setLastPayload({ type: 'create', data: formData });
        await companyAPI.createCompany(formData);
        alert('企業を作成しました');
      }
      onSaveSuccess();
    } catch (err) {
      console.error('Failed to save company:', err);
      setServerError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async () => {
    if (!lastPayload) return;
    setServerError(null);
    try {
      if (lastPayload.type === 'update') {
        await companyAPI.updateCompany(lastPayload.id, lastPayload.data);
      } else {
        await companyAPI.createCompany(lastPayload.data);
      }
      onSaveSuccess();
    } catch (err) {
      setServerError(extractApiError(err));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? '企業情報の編集' : '新規企業の作成'}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <AlertBanner message={serverError} onRetry={handleRetry} onClose={() => setServerError(null)} />
          {/* 企業名 */}
          <div className="form-group">
            <label>企業名 *</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="例: 株式会社トポロジ"
              className={errors.company_name ? 'error' : ''}
            />
            {errors.company_name && (
              <span className="error-message">{errors.company_name}</span>
            )}
          </div>

          {/* カラー */}
          <div className="form-group">
            <label>カレンダー表示色 *</label>
            <div className="color-picker-container">
              <input
                type="color"
                value={formData.calendar_color}
                onChange={(e) => handleChange('calendar_color', e.target.value)}
                className="color-picker"
              />
              <input
                type="text"
                value={formData.calendar_color}
                onChange={(e) => handleChange('calendar_color', e.target.value)}
                placeholder="#007bff"
                className="color-text-input"
              />
            </div>
            {errors.calendar_color && (
              <span className="error-message">{errors.calendar_color}</span>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={saving}>
            キャンセル
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyModal;
